import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE_URL, getAuthHeaders } from '../utils/api';
import './HorizonManagementModal.css';

function HorizonManagementModal({ user, onClose, onUpdate }) {
  const [allHorizons, setAllHorizons] = useState([]);
  const [userHorizons, setUserHorizons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const headers = await getAuthHeaders();
      
      // Load all available horizons
      const horizonsResponse = await fetch(`${API_BASE_URL}/casc/v1/api/horizon-management/horizons`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...headers,
        },
        body: JSON.stringify({}),
      });

      if (horizonsResponse.ok) {
        const horizonsData = await horizonsResponse.json();
        setAllHorizons(horizonsData.horizons || []);
      }

      // Load user's assigned horizons
      const userHorizonsResponse = await fetch(`${API_BASE_URL}/casc/v1/api/horizon-management/user-horizons`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...headers,
        },
        body: JSON.stringify({ user_id: user.id }),
      });

      if (userHorizonsResponse.ok) {
        const userHorizonsData = await userHorizonsResponse.json();
        setUserHorizons(userHorizonsData.horizons || []);
      }
    } catch (error) {
      console.error('Error loading horizon data:', error);
      setMessage({ type: 'error', text: 'Error loading horizon data' });
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      loadData();
    }
  }, [user, loadData]);

  const handleAddHorizon = async (suffix) => {
    try {
      setSaving(true);
      setMessage({ type: '', text: '' });
      const headers = await getAuthHeaders();
      
      const response = await fetch(`${API_BASE_URL}/casc/v1/api/horizon-management/add-horizon`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...headers,
        },
        body: JSON.stringify({
          user_id: user.id,
          suffix: suffix,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setMessage({ type: 'success', text: `Horizon ${suffix} added successfully` });
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
        await loadData();
        if (onUpdate) onUpdate();
      } else {
        setMessage({ type: 'error', text: data.message || data.error || 'Failed to add horizon' });
        setTimeout(() => setMessage({ type: '', text: '' }), 5000);
      }
    } catch (error) {
      console.error('Error adding horizon:', error);
      setMessage({ type: 'error', text: 'Error adding horizon' });
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveHorizon = async (suffix) => {
    if (!window.confirm(`Are you sure you want to remove horizon "${suffix}" from ${user.email}?`)) {
      return;
    }

    try {
      setSaving(true);
      setMessage({ type: '', text: '' });
      const headers = await getAuthHeaders();
      
      const response = await fetch(`${API_BASE_URL}/casc/v1/api/horizon-management/remove-horizon`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...headers,
        },
        body: JSON.stringify({
          user_id: user.id,
          suffix: suffix,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setMessage({ type: 'success', text: `Horizon ${suffix} removed successfully` });
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
        await loadData();
        if (onUpdate) onUpdate();
      } else {
        setMessage({ type: 'error', text: data.message || data.error || 'Failed to remove horizon' });
        setTimeout(() => setMessage({ type: '', text: '' }), 5000);
      }
    } catch (error) {
      console.error('Error removing horizon:', error);
      setMessage({ type: 'error', text: 'Error removing horizon' });
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    } finally {
      setSaving(false);
    }
  };

  const userHorizonSuffixes = new Set(userHorizons.map(h => h.suffix));
  const availableHorizons = allHorizons.filter(h => !userHorizonSuffixes.has(h.suffix));

  return (
    <div className="horizon-management-modal-overlay">
      <div className="horizon-management-modal">
        <div className="horizon-management-modal-header">
          <h2>
            <i className="fas fa-database"></i> Horizon Management
          </h2>
          <button className="modal-close-btn" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="horizon-management-modal-content">
          <div className="horizon-management-user-info">
            <strong>User:</strong> {user.email}
          </div>

          {message.text && (
            <div className={`horizon-message alert alert-${message.type}`}>
              <i className={`fas fa-${message.type === 'success' ? 'check-circle' : 'exclamation-circle'}`}></i>
              {message.text}
            </div>
          )}

          {loading ? (
            <div className="horizon-management-loading">
              <i className="fas fa-spinner fa-spin"></i> Loading...
            </div>
          ) : (
            <>
              {/* User's Current Horizons */}
              <div className="horizon-section">
                <h3>
                  <i className="fas fa-check-circle"></i> Assigned Horizons ({userHorizons.length})
                </h3>
                {userHorizons.length === 0 ? (
                  <div className="horizon-empty-state">
                    <p>No horizons assigned. Add horizons from the available list below.</p>
                  </div>
                ) : (
                  <div className="horizon-list">
                    {userHorizons.map((horizon) => (
                      <div key={horizon.suffix} className="horizon-item assigned">
                        <div className="horizon-item-info">
                          <span className="horizon-name">{horizon.suffix}</span>
                          {horizon.description && (
                            <span className="horizon-description">{horizon.description}</span>
                          )}
                        </div>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => handleRemoveHorizon(horizon.suffix)}
                          disabled={saving}
                        >
                          <i className="fas fa-times"></i> Remove
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Available Horizons */}
              <div className="horizon-section">
                <h3>
                  <i className="fas fa-plus-circle"></i> Available Horizons ({availableHorizons.length})
                </h3>
                {availableHorizons.length === 0 ? (
                  <div className="horizon-empty-state">
                    <p>All horizons are already assigned to this user.</p>
                  </div>
                ) : (
                  <div className="horizon-list">
                    {availableHorizons.map((horizon) => (
                      <div key={horizon.suffix} className="horizon-item available">
                        <div className="horizon-item-info">
                          <span className="horizon-name">{horizon.suffix}</span>
                          {horizon.description && (
                            <span className="horizon-description">{horizon.description}</span>
                          )}
                        </div>
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => handleAddHorizon(horizon.suffix)}
                          disabled={saving}
                        >
                          <i className="fas fa-plus"></i> Add
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        <div className="horizon-management-modal-footer">
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default HorizonManagementModal;
